import {
  Badge,
  Button,
  Drawer,
  DrawerBody,
  IconButton,
  Popover,
  Text,
  Tooltip,
  useBreakpoints,
} from "@aurem/ui";
import { FilterIcon } from "lucide-react";
import { type ReactNode, useState } from "react";
import { useTranslations } from "../i18n";

type FiltersPopoverProps = {
  children: ReactNode;
  filtersCount?: number;
  onResetFilters?: () => void;
};

export const FiltersPopover = ({
  children,
  filtersCount,
  onResetFilters,
}: FiltersPopoverProps) => {
  const [open, setOpen] = useState(false);
  const { t } = useTranslations();
  const { isMobile } = useBreakpoints();

  const trigger = (
    <Tooltip
      content={t("components.queryToolbar.filters")}
      placement="bottom-start"
    >
      <Badge content={filtersCount ?? 0} color="primary">
        <IconButton
          variant="ghost"
          color="neutral"
          content={<FilterIcon size="1.25rem" />}
          aria-label={t("components.queryToolbar.ariaFilters")}
        />
      </Badge>
    </Tooltip>
  );

  const header = (
    <div className="flex items-center justify-between p-3">
      <Text size="md" weight="medium">
        {t("components.queryToolbar.filters")}
      </Text>
      {onResetFilters && (
        <Button
          variant="text"
          color="neutral"
          size="sm"
          onClick={onResetFilters}
        >
          {t("components.queryToolbar.reset")}
        </Button>
      )}
    </div>
  );

  const content = (
    <div className="flex flex-col gap-2">
      {header}
      <div className="flex flex-col px-3 pb-3">{children}</div>
    </div>
  );

  if (isMobile) {
    return (
      <div>
        <div onClick={() => setOpen(true)}>{trigger}</div>
        <Drawer
          open={open}
          onOpenChange={setOpen}
          side="bottom"
          title={t("components.queryToolbar.filters")}
        >
          <DrawerBody>{children}</DrawerBody>
        </Drawer>
      </div>
    );
  }

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      content={content}
      placement="bottom-start"
      contentClassName="p-2"
      maxWidth={366}
    >
      {trigger}
    </Popover>
  );
};
